import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    PieChart, Pie, Cell,
} from "recharts";
import {
    Activity, Eye, Calendar, TrendingUp, Timer, MousePointer, Globe, Monitor,
    Film, Info, Users, LogOut, ExternalLink, AlertTriangle, ImageOff,
    RefreshCw, Sparkles,
} from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import AnalyticsRangeFilter from "@/components/AnalyticsRangeFilter";
import WelcomePopupWidget from "@/components/admin/WelcomePopupWidget";

type Overview = {
    live_visitors: number;
    today: number;
    yesterday: number;
    days_7: number;
    days_30: number;
    avg_session_seconds: number;
    conversion_rate: number;
    top_pages: Array<{ page_path: string; views: number; unique_sessions: number }>;
    top_referrers: Array<{
        source: string;
        label: string;
        category: string;
        host: string | null;
        views: number;
    }>;
    devices: Array<{ device: string; sessions: number }>;
    countries: Array<{ country: string; sessions: number }>;
    generated_at: string;
};

type SeriesPoint = { date: string; visitors: number };

type PageDetail = {
    page_path: string; views: number; sessions: number; avg_session_seconds: number;
};

type VtStat = {
    id: string; code: string; number?: number | null; name: string; active: boolean;
    scans: number; requests: number; conversion_rate: number;
};

type MediaStat = {
    media_id: string;
    viewer_type: "video360" | "photo360" | "before_after_360";
    media_title?: string | null;
    opens: number;
    plays: number;
    completes: number;
    interactions_count: number;
    avg_watched_seconds: number;
    milestones_reached: { "25": number; "50": number; "75": number; "100": number };
};

type ExitAnalysis = {
    range: string;
    total_sessions: number;
    exits: Array<{ path: string; count: number; percentage: number }>;
    actions: Array<{ type: string; count: number; percentage: number }>;
};

type CtaImpact = {
    range: string;
    shown: number;
    clicked: number;
    dismissed: number;
    click_rate: number;
    dismiss_rate: number;
    by_trigger: Record<string, { shown: number; clicked: number; dismissed: number }>;
};

type AktionImpact = {
    range: string;
    banner_shown: number;
    banner_clicks: number;
    banner_click_rate: number;
    section_shown: number;
    section_clicks: number;
    section_click_rate: number;
    form_submits: number;
    form_submits_from_db: number;
    conversion_rate: number;
};

/** Menschenlesbare Labels für die letzte Aktion vor Abbruch.
 *  Kategorien: siehe `_action_category()` im Backend. */
const EXIT_ACTION_LABEL: Record<string, string> = {
    page_view: "Reines Seiten-Ansehen",
    heartbeat: "Inaktiv verlassen (Idle)",
    conversion: "Conversion abgeschlossen",
    viewer_open_video360: "360°-Video geöffnet",
    viewer_open_photo360: "360°-Foto geöffnet",
    viewer_open_before_after_360: "Vorher/Nachher-360° geöffnet",
    viewer_play: "360°-Video gestartet",
    viewer_pause: "360°-Video pausiert",
    viewer_close: "360°-Viewer geschlossen",
    viewer_complete: "360°-Video zu Ende gesehen",
    viewer_interaction: "In 360°-Viewer bewegt",
    viewer_fullscreen_enter: "Vollbild aktiviert",
    viewer_watch_milestone: "Sehdauer-Meilenstein",
    button_click: "Button geklickt",
    contact_submit: "Kontaktformular gesendet",
    interaction: "Sonstige Interaktion",
    unknown: "Unbekannt",
};

// Emoji-Flaggen aus ISO-3166-Alpha-2-Code.
function flagFromISO(iso: string | null | undefined): string {
    if (!iso || iso.length !== 2) return "🌐";
    return String.fromCodePoint(...iso.toUpperCase().split("").map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)));
}

function formatDuration(sec: number): string {
    if (!sec || sec <= 0) return "–";
    if (sec < 60) return `${sec.toFixed(0)}s`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}m ${s.toString().padStart(2, "0")}s`;
}

const REF_LABEL: Record<string, string> = {
    direct: "Direktzugriff", search: "Suchmaschine", social: "Social Media",
    referrer: "Verweisender Link", internal: "Intern",
};

const DEVICE_LABEL: Record<string, string> = {
    desktop: "Desktop", mobile: "Mobil", tablet: "Tablet", unknown: "Unbekannt",
};

const DEVICE_COLORS = ["hsl(var(--hf-gold))", "hsl(var(--hf-ink))", "hsl(var(--hf-muted))", "hsl(var(--hf-line))"];

const VIEWER_LABEL: Record<string, string> = {
    video360: "Video 360°",
    photo360: "Foto 360°",
    before_after_360: "Vorher/Nachher 360°",
};

function truncateLabel(s: string, max = 60): string {
    if (!s) return "–";
    return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

function MediaSortHeader({
    label, k, sortKey, onSort, align = "left",
}: {
    label: string;
    k: keyof MediaStat | "milestone_progress";
    sortKey: keyof MediaStat | "milestone_progress";
    onSort: (k: keyof MediaStat | "milestone_progress") => void;
    align?: "left" | "right";
}) {
    const active = sortKey === k;
    return (
        <th
            className={`py-2 text-hf-muted font-normal text-${align} cursor-pointer select-none`}
            onClick={() => onSort(k)}
        >
            <span className={active ? "text-hf-ink" : ""}>{label}{active ? " ↓" : ""}</span>
        </th>
    );
}

function MilestoneBar({
    milestones, opens,
}: {
    milestones: MediaStat["milestones_reached"]; opens: number;
}) {
    if (!opens || opens === 0) return <div className="text-xs text-hf-muted">–</div>;
    const pcts = [25, 50, 75, 100].map((m) => {
        const reached = milestones?.[String(m) as "25" | "50" | "75" | "100"] || 0;
        return Math.min(100, Math.round((reached / opens) * 100));
    });
    return (
        <div className="flex gap-0.5" style={{ minWidth: 96 }}>
            {pcts.map((pct, i) => (
                <div
                    key={i}
                    className="h-2 rounded-sm flex-1"
                    style={{
                        background: `linear-gradient(90deg, hsl(var(--hf-gold)) 0%, hsl(var(--hf-gold)) ${pct}%, hsl(var(--hf-line)) ${pct}%, hsl(var(--hf-line)) 100%)`,
                    }}
                    title={`${[25, 50, 75, 100][i]}%: ${pct}%`}
                />
            ))}
        </div>
    );
}

export default function AdminAnalyticsPage() {
    const [overview, setOverview] = useState<Overview | null>(null);
    const [series, setSeries] = useState<SeriesPoint[]>([]);
    const [pages, setPages] = useState<PageDetail[]>([]);
    const [vts, setVts] = useState<VtStat[]>([]);
    const [media, setMedia] = useState<MediaStat[]>([]);
    const [mediaSortKey, setMediaSortKey] = useState<keyof MediaStat | "milestone_progress">("opens");
    const [mediaRange, setMediaRange] = useState<string>("30d");
    const [mediaLoading, setMediaLoading] = useState(false);
    const [exitData, setExitData] = useState<ExitAnalysis | null>(null);
    const [exitRange, setExitRange] = useState<string>("30d");
    const [exitLoading, setExitLoading] = useState(false);
    const [ctaImpact, setCtaImpact] = useState<CtaImpact | null>(null);
    const [ctaRange, setCtaRange] = useState<string>("30d");
    const [ctaLoading, setCtaLoading] = useState(false);
    const [aktionImpact, setAktionImpact] = useState<AktionImpact | null>(null);
    const [aktionRange, setAktionRange] = useState<string>("30d");
    const [aktionLoading, setAktionLoading] = useState(false);
    /** Range-State für das Welcome-Popup-Widget (unabhängig, damit man
     *  Choice-Verteilung fokussiert auf ein Zeitfenster ansehen kann,
     *  ohne die anderen Sektionen mit umzuschalten). */
    const [welcomeRange, setWelcomeRange] = useState<string>("7d");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    /** Overview isoliert nachladen — nach einem Backfill nutzen wir das,
     *  damit die neue Herkunfts-Verteilung sofort sichtbar wird, ohne den
     *  gesamten Page-Reload. */
    const reloadOverview = async () => {
        const ovRes = await api<Overview>("/api/admin/analytics/overview");
        if (ovRes.ok && ovRes.data) setOverview(ovRes.data);
    };

    useEffect(() => {
        void (async () => {
            setLoading(true);
            const [ovRes, sRes, pRes, vRes] = await Promise.all([
                api<Overview>("/api/admin/analytics/overview"),
                api<{ series: SeriesPoint[] }>("/api/admin/analytics/timeseries?range=30d"),
                api<{ pages: PageDetail[] }>("/api/admin/analytics/pages?range=7d"),
                api<{ representatives: VtStat[] }>("/api/admin/analytics/vt"),
            ]);
            if (ovRes.ok && ovRes.data) setOverview(ovRes.data);
            else setError(ovRes.error || "Übersicht konnte nicht geladen werden.");
            if (sRes.ok && sRes.data) setSeries(sRes.data.series || []);
            if (pRes.ok && pRes.data) setPages(pRes.data.pages || []);
            if (vRes.ok && vRes.data) setVts(vRes.data.representatives || []);
            setLoading(false);
        })();
    }, []);

    // 360°-Medien: separater Fetch, reagiert auf lokalen Range-Toggle
    // (`mediaRange`) — analog zu Exit-Analyse / CTA-Impact / Aktion-Impact.
    useEffect(() => {
        let cancelled = false;
        setMediaLoading(true);
        void (async () => {
            const res = await api<{ media: MediaStat[] }>(
                `/api/admin/analytics/media?range=${encodeURIComponent(mediaRange)}`,
            );
            if (cancelled) return;
            setMediaLoading(false);
            if (res.ok && res.data) setMedia(res.data.media || []);
        })();
        return () => { cancelled = true; };
    }, [mediaRange]);

    // Exit-Analyse: separater Fetch, reagiert auf lokalen Range-Toggle.
    useEffect(() => {
        let cancelled = false;
        setExitLoading(true);
        void (async () => {
            const res = await api<ExitAnalysis>(
                `/api/admin/analytics/exit-analysis?range=${exitRange}`,
            );
            if (cancelled) return;
            setExitLoading(false);
            if (res.ok && res.data) setExitData(res.data);
        })();
        return () => { cancelled = true; };
    }, [exitRange]);

    // 360°-CTA-Wirkung: analog, eigener Range-Toggle.
    useEffect(() => {
        let cancelled = false;
        setCtaLoading(true);
        void (async () => {
            const res = await api<CtaImpact>(
                `/api/admin/analytics/cta-impact?range=${ctaRange}`,
            );
            if (cancelled) return;
            setCtaLoading(false);
            if (res.ok && res.data) setCtaImpact(res.data);
        })();
        return () => { cancelled = true; };
    }, [ctaRange]);

    // Referenz-Aktion-Wirkung.
    useEffect(() => {
        let cancelled = false;
        setAktionLoading(true);
        void (async () => {
            const res = await api<AktionImpact>(
                `/api/admin/analytics/aktion-impact?range=${aktionRange}`,
            );
            if (cancelled) return;
            setAktionLoading(false);
            if (res.ok && res.data) setAktionImpact(res.data);
        })();
        return () => { cancelled = true; };
    }, [aktionRange]);

    const conversionPct = useMemo(() => {
        if (!overview) return "0.0%";
        return (overview.conversion_rate * 100).toFixed(1) + "%";
    }, [overview]);

    const sortedMedia = useMemo(() => {
        const arr = [...media];
        arr.sort((a, b) => {
            if (mediaSortKey === "milestone_progress") {
                const aPct = a.opens ? (a.milestones_reached?.["100"] || 0) / a.opens : 0;
                const bPct = b.opens ? (b.milestones_reached?.["100"] || 0) / b.opens : 0;
                return bPct - aPct;
            }
            const av = a[mediaSortKey as keyof MediaStat];
            const bv = b[mediaSortKey as keyof MediaStat];
            if (typeof av === "number" && typeof bv === "number") return bv - av;
            return String(bv ?? "").localeCompare(String(av ?? ""));
        });
        return arr;
    }, [media, mediaSortKey]);

    return (
        <div data-testid="admin-analytics">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Reichweite</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Analytics</h1>
                    <p className="mt-2 text-hf-muted max-w-2xl">
                        Cookiefreie, DSGVO-freundliche Reichweitenmessung.
                        IPs werden sofort anonymisiert (letztes Oktett = 0), Session-IDs
                        gehasht und nur für die Dauer eines Besuchs vorgehalten.
                    </p>
                </div>
                <Link
                    to="/admin/analytics/sessions"
                    className="hf-btn hf-btn-primary"
                    data-testid="admin-analytics-sessions-link"
                >
                    <Users size={14} /> Sessions ansehen
                </Link>
            </div>

            <div
                className="mt-4 p-3 rounded-sm text-xs text-hf-muted"
                style={{ background: "hsl(var(--hf-gold-soft) / 0.35)", border: "1px solid hsl(var(--hf-gold) / 0.3)" }}
            >
                <Info size={12} className="inline mr-1" />
                Seit dem letzten Update filtert das Backend Bot- und Automation-Traffic
                automatisch heraus (Silent-Drop). Historische Zahlen (letzte 7 – 30 Tage)
                können noch alten Bot-Traffic enthalten — der TTL-Index löscht sie
                nach 90 Tagen. Für die genaue Herkunftsanalyse siehe &bdquo;Sessions ansehen&ldquo;.
            </div>

            {error && (
                <div
                    className="mt-6 p-3 rounded text-sm"
                    style={{
                        background: "hsl(var(--hf-danger) / 0.12)",
                        color: "hsl(var(--hf-danger))",
                        border: "1px solid hsl(var(--hf-danger) / 0.3)",
                    }}
                    data-testid="admin-analytics-error"
                >
                    {error}
                </div>
            )}

            {loading && (
                <div className="mt-8 text-hf-muted text-sm" data-testid="admin-analytics-loading">
                    Wird geladen…
                </div>
            )}

            {overview && (
                <>
                    {/* KPI-Kacheln */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-8">
                        <KpiTile
                            testId="kpi-live"
                            icon={<Activity size={16} />}
                            label="Live (5 min)"
                            value={overview.live_visitors}
                            tone="highlight"
                        />
                        <KpiTile testId="kpi-today" icon={<Eye size={16} />} label="Heute" value={overview.today} />
                        <KpiTile testId="kpi-yesterday" icon={<Calendar size={16} />} label="Gestern" value={overview.yesterday} />
                        <KpiTile testId="kpi-7d" icon={<TrendingUp size={16} />} label="7 Tage" value={overview.days_7} />
                        <KpiTile testId="kpi-30d" icon={<TrendingUp size={16} />} label="30 Tage" value={overview.days_30} />
                        <KpiTile
                            testId="kpi-conversion"
                            icon={<MousePointer size={16} />}
                            label="Conversion 7T"
                            value={conversionPct}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                        <KpiTile
                            testId="kpi-avg-duration"
                            icon={<Timer size={16} />}
                            label="Ø-Verweildauer"
                            value={formatDuration(overview.avg_session_seconds)}
                        />
                    </div>

                    {/* Willkommens-Popup-Widget · high priority KPI, direkt
                     *  unter den KPI-Kacheln, damit Choice-Verteilung und
                     *  Konversionsrate ohne Scrollen sichtbar sind. */}
                    <div className="mt-8">
                        <WelcomePopupWidget
                            title="Willkommens-Popup"
                            externalRange={welcomeRange}
                            onExternalRangeChange={setWelcomeRange}
                        />
                    </div>

                    {/* Zeitverlauf */}
                    <div
                        className="mt-8 p-6 rounded-sm"
                        style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="analytics-timeseries"
                    >
                        <div className="hf-eyebrow">Zeitverlauf</div>
                        <div className="font-display text-xl mt-1">Besucher · letzte 30 Tage</div>
                        <div className="h-72 mt-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--hf-line))" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--hf-muted))" }} />
                                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--hf-muted))" }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{
                                            background: "hsl(var(--hf-ink))",
                                            border: "1px solid hsl(var(--hf-dark-2))",
                                            color: "hsl(var(--hf-bg))",
                                            fontSize: 12,
                                        }}
                                    />
                                    <Line type="monotone" dataKey="visitors" stroke="hsl(var(--hf-gold))" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Grid: Pages + Referrers */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                        <PanelCard title="Top Seiten (7T)" testId="analytics-top-pages" icon={<Eye size={14} />}>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                        <th className="text-left py-2 text-hf-muted font-normal">Seite</th>
                                        <th className="text-right py-2 text-hf-muted font-normal">Views</th>
                                        <th className="text-right py-2 text-hf-muted font-normal">Sessions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pages.slice(0, 8).map((p) => (
                                        <tr key={p.page_path} style={{ borderBottom: "1px solid hsl(var(--hf-line) / 0.5)" }}>
                                            <td className="py-2 font-mono text-xs truncate max-w-[200px]">{p.page_path}</td>
                                            <td className="py-2 text-right">{p.views}</td>
                                            <td className="py-2 text-right text-hf-muted">{p.sessions}</td>
                                        </tr>
                                    ))}
                                    {pages.length === 0 && (
                                        <tr><td colSpan={3} className="py-4 text-center text-hf-muted">Noch keine Daten.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </PanelCard>
                        <PanelCard title="Herkunft (7T)" testId="analytics-referrers" icon={<Globe size={14} />}>
                            <SourceBackfillPanel onReclassified={reloadOverview} />
                            <table className="w-full text-sm">
                                <thead>
                                    <tr style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                        <th className="text-left py-2 text-hf-muted font-normal">Quelle</th>
                                        <th className="text-left py-2 text-hf-muted font-normal">Host</th>
                                        <th className="text-right py-2 text-hf-muted font-normal">Views</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.top_referrers.map((r, i) => (
                                        <tr key={i} style={{ borderBottom: "1px solid hsl(var(--hf-line) / 0.5)" }} data-testid={`referrer-row-${r.source}`}>
                                            <td className="py-2">{r.label || REF_LABEL[r.category] || r.category}</td>
                                            <td className="py-2 text-hf-muted text-xs">{r.host || "–"}</td>
                                            <td className="py-2 text-right">{r.views}</td>
                                        </tr>
                                    ))}
                                    {overview.top_referrers.length === 0 && (
                                        <tr><td colSpan={3} className="py-4 text-center text-hf-muted">Noch keine Daten.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </PanelCard>
                    </div>

                    {/* Grid: Devices + Countries */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                        <PanelCard title="Geräte (7T)" testId="analytics-devices" icon={<Monitor size={14} />}>
                            {overview.devices.length === 0 ? (
                                <div className="py-8 text-center text-hf-muted text-sm">Noch keine Daten.</div>
                            ) : (
                                <div className="h-56">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={overview.devices.map((d) => ({
                                                    name: DEVICE_LABEL[d.device] || d.device,
                                                    value: d.sessions,
                                                }))}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%" cy="50%"
                                                innerRadius={40}
                                                outerRadius={80}
                                                paddingAngle={2}
                                            >
                                                {overview.devices.map((_, i) => (
                                                    <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    background: "hsl(var(--hf-ink))",
                                                    border: "1px solid hsl(var(--hf-dark-2))",
                                                    color: "hsl(var(--hf-bg))",
                                                    fontSize: 12,
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-3 text-xs">
                                {overview.devices.map((d, i) => (
                                    <div key={d.device} className="flex items-center gap-2">
                                        <span
                                            className="inline-block w-3 h-3 rounded-sm"
                                            style={{ background: DEVICE_COLORS[i % DEVICE_COLORS.length] }}
                                        />
                                        <span>{DEVICE_LABEL[d.device] || d.device}</span>
                                        <span className="text-hf-muted">({d.sessions})</span>
                                    </div>
                                ))}
                            </div>
                        </PanelCard>
                        <PanelCard title="Länder (7T)" testId="analytics-countries" icon={<Globe size={14} />}>
                            {overview.countries.length === 0 ? (
                                <div className="py-8 text-center text-hf-muted text-sm">
                                    Kein Country-Lookup aktiv (GEOIP_DB_PATH nicht gesetzt).
                                </div>
                            ) : (
                                <ul className="divide-y" style={{ borderColor: "hsl(var(--hf-line))" }}>
                                    {overview.countries.slice(0, 10).map((c) => (
                                        <li key={c.country} className="flex items-center justify-between py-2 text-sm">
                                            <span className="flex items-center gap-2">
                                                <span className="text-lg">{flagFromISO(c.country)}</span>
                                                <span>{c.country}</span>
                                            </span>
                                            <span className="text-hf-muted">{c.sessions}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </PanelCard>
                    </div>

                    {/* Abbruch-Analyse: Top Exit-Pages + letzte Aktion vor Abbruch.
                     *  Simpel: 2-Spalten-Grid mit dezenten Balken; Range-Toggle
                     *  auf dieser Card. Basis: letztes Event pro Session. */}
                    <div className="mt-8">
                        <ExitAnalysisSection
                            data={exitData}
                            range={exitRange}
                            onRangeChange={setExitRange}
                            loading={exitLoading}
                        />
                    </div>

                    {/* 360°-CTA-Wirkung: Anzeigen / Klicks / Dismissals + Split
                     *  nach Trigger-Typ (photo360 / video360). */}
                    <div className="mt-8">
                        <CtaImpactSection
                            data={ctaImpact}
                            range={ctaRange}
                            onRangeChange={setCtaRange}
                            loading={ctaLoading}
                        />
                    </div>

                    {/* Referenz-Aktion-Wirkung: Banner-/Sektion-Impressions,
                     *  Klicks, Anfragen aus der Aktion, Konversion in %. */}
                    <div className="mt-8">
                        <AktionImpactSection
                            data={aktionImpact}
                            range={aktionRange}
                            onRangeChange={setAktionRange}
                            loading={aktionLoading}
                        />
                    </div>

                    {/* 360°-Medien-Sektion */}
                    <div
                        className="mt-8 p-6 rounded-sm"
                        style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="analytics-media"
                    >
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div>
                                <div className="hf-eyebrow">Immersion</div>
                                <div className="font-display text-xl mt-1 flex items-center gap-2">
                                    <Film size={18} /> 360°-Medien · welche Aufnahmen fesseln?
                                </div>
                            </div>
                            <div className="inline-flex flex-col items-end gap-2">
                                <AnalyticsRangeFilter
                                    value={mediaRange}
                                    onChange={setMediaRange}
                                    testIdPrefix="analytics-media-range"
                                />
                                <div
                                    className="flex items-center gap-2 text-xs text-hf-muted max-w-md"
                                    title="Zeigt an, welche 360°-Medien Ihre Besucher am längsten fesseln. Nutze das, um deine besten Aufnahmen zu identifizieren."
                                    data-testid="analytics-media-info"
                                >
                                    <Info size={14} className="shrink-0" />
                                    <span>Sortiert nach Aufrufen — höhere Ø-Sehdauer und Milestone-100 % markieren die stärksten Aufnahmen.</span>
                                </div>
                            </div>
                        </div>

                        {mediaLoading ? (
                            <div className="mt-6 py-8 text-center text-hf-muted text-sm" data-testid="analytics-media-loading">
                                Wird geladen…
                            </div>
                        ) : media.length === 0 ? (
                            <div className="mt-6 py-8 text-center text-hf-muted text-sm">
                                Noch keine 360°-Viewer-Aufrufe erfasst.
                            </div>
                        ) : (
                            <div className="mt-6 overflow-x-auto">
                                <table className="w-full text-sm" data-testid="analytics-media-table">
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                            <MediaSortHeader label="Medium" k="media_title" sortKey={mediaSortKey} onSort={setMediaSortKey} />
                                            <MediaSortHeader label="Typ" k="viewer_type" sortKey={mediaSortKey} onSort={setMediaSortKey} />
                                            <MediaSortHeader label="Aufrufe" k="opens" sortKey={mediaSortKey} onSort={setMediaSortKey} align="right" />
                                            <MediaSortHeader label="Voll" k="completes" sortKey={mediaSortKey} onSort={setMediaSortKey} align="right" />
                                            <MediaSortHeader label="Ø-Sehdauer" k="avg_watched_seconds" sortKey={mediaSortKey} onSort={setMediaSortKey} align="right" />
                                            <MediaSortHeader label="Milestones" k="milestone_progress" sortKey={mediaSortKey} onSort={setMediaSortKey} />
                                            <MediaSortHeader label="Interaktionen" k="interactions_count" sortKey={mediaSortKey} onSort={setMediaSortKey} align="right" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedMedia.map((m) => {
                                            const label = truncateLabel(m.media_title || m.media_id);
                                            const opens = m.opens || 0;
                                            const m100 = m.milestones_reached?.["100"] || 0;
                                            const milestonePct = opens > 0
                                                ? Math.round((m100 / opens) * 100)
                                                : 0;
                                            return (
                                                <tr
                                                    key={m.media_id}
                                                    style={{ borderBottom: "1px solid hsl(var(--hf-line) / 0.5)" }}
                                                    data-testid={`analytics-media-row-${m.media_id}`}
                                                >
                                                    <td className="py-2 pr-3">
                                                        <div className="text-hf-ink truncate max-w-[260px]" title={m.media_title || m.media_id}>
                                                            {label}
                                                        </div>
                                                        <div className="text-[10px] text-hf-muted font-mono truncate max-w-[260px]">
                                                            {m.media_id}
                                                        </div>
                                                    </td>
                                                    <td className="py-2 text-xs">
                                                        <span
                                                            className="px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider"
                                                            style={{
                                                                background: "hsl(var(--hf-surface))",
                                                                color: "hsl(var(--hf-ink))",
                                                            }}
                                                        >
                                                            {VIEWER_LABEL[m.viewer_type] || m.viewer_type}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 text-right">{opens}</td>
                                                    <td className="py-2 text-right">{m.completes}</td>
                                                    <td className="py-2 text-right">{formatDuration(m.avg_watched_seconds || 0)}</td>
                                                    <td className="py-2">
                                                        <MilestoneBar milestones={m.milestones_reached} opens={opens} />
                                                        <div className="text-[10px] text-hf-muted mt-1">
                                                            {milestonePct}% erreichen 100 %
                                                        </div>
                                                    </td>
                                                    <td className="py-2 text-right">{m.interactions_count}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Vertreter-Sektion */}
                    <div
                        className="mt-8 p-6 rounded-sm"
                        style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="analytics-vt"
                    >
                        <div className="hf-eyebrow">Vertriebspartner</div>
                        <div className="font-display text-xl mt-1">Vertreter · Scans & Anfragen</div>
                        <table className="w-full text-sm mt-4">
                            <thead>
                                <tr style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                    <th className="text-left py-2 text-hf-muted font-normal">Nr.</th>
                                    <th className="text-left py-2 text-hf-muted font-normal">Vertreter</th>
                                    <th className="text-right py-2 text-hf-muted font-normal">Scans</th>
                                    <th className="text-right py-2 text-hf-muted font-normal">Anfragen</th>
                                    <th className="text-right py-2 text-hf-muted font-normal">Conversion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vts.map((v) => (
                                    <tr key={v.id} style={{ borderBottom: "1px solid hsl(var(--hf-line) / 0.5)" }}>
                                        <td className="py-2">
                                            <span
                                                className="inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 rounded-sm font-display text-sm"
                                                style={{
                                                    background: "hsl(var(--hf-gold) / 0.22)",
                                                    color: "hsl(var(--hf-ink))",
                                                    border: "1px solid hsl(var(--hf-gold) / 0.4)",
                                                }}
                                            >
                                                {v.number != null ? v.number : "–"}
                                            </span>
                                        </td>
                                        <td className="py-2">{v.name}</td>
                                        <td className="py-2 text-right">{v.scans}</td>
                                        <td className="py-2 text-right">{v.requests}</td>
                                        <td className="py-2 text-right">{(v.conversion_rate * 100).toFixed(1)}%</td>
                                    </tr>
                                ))}
                                {vts.length === 0 && (
                                    <tr><td colSpan={5} className="py-6 text-center text-hf-muted">
                                        Noch keine Vertreter angelegt.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function KpiTile({
    testId, icon, label, value, tone = "neutral",
}: {
    testId: string; icon: React.ReactNode; label: string; value: string | number;
    tone?: "neutral" | "highlight";
}) {
    return (
        <div
            data-testid={testId}
            className="p-4 rounded-sm"
            style={{
                background: tone === "highlight" ? "hsl(var(--hf-gold-soft) / 0.5)" : "hsl(var(--hf-bg))",
                border: `1px solid ${tone === "highlight" ? "hsl(var(--hf-gold) / 0.5)" : "hsl(var(--hf-line))"}`,
            }}
        >
            <div className="flex items-center gap-2 text-hf-muted text-xs">
                {icon}<span className="hf-eyebrow" style={{ fontSize: 10 }}>{label}</span>
            </div>
            <div className="mt-2 font-display text-2xl md:text-3xl text-hf-ink">{value}</div>
        </div>
    );
}

/** Kleine Aktion-Card im Header der Herkunft-Sektion. Bietet zwei Buttons:
 *  1) „Vorschau" — Dry-Run: zeigt, wie viele Events reklassifiziert würden.
 *  2) „Jetzt reklassifizieren" — Confirm-Dialog, dann harter Backfill.
 *  Nach erfolgreichem Backfill wird `onReclassified` gefeuert (Overview-Reload). */
type BackfillResult = {
    dry_run: boolean;
    force: boolean;
    total_scanned: number;
    reclassified: number;
    by_source: Record<string, number>;
    duration_ms: number;
};

const BACKFILL_SOURCE_LABELS: Record<string, string> = {
    meta: "Meta",
    tiktok: "TikTok",
    "google-ads": "Google Ads",
    google: "Google-Suche",
    youtube: "YouTube",
    bing: "Bing",
    linkedin: "LinkedIn",
    twitter: "X (Twitter)",
    pinterest: "Pinterest",
    reddit: "Reddit",
    snapchat: "Snapchat",
    whatsapp: "WhatsApp",
    telegram: "Telegram",
    email: "E-Mail",
    sms: "SMS",
    qr: "QR-Code",
    print: "Print",
    social: "Social",
    search: "Suche",
    referrer: "Andere",
    direct: "Direkt",
};

function formatBackfillSources(by: Record<string, number>): string {
    const entries = Object.entries(by).sort((a, b) => b[1] - a[1]).slice(0, 6);
    if (entries.length === 0) return "keine Daten";
    return entries
        .map(([k, n]) => `${BACKFILL_SOURCE_LABELS[k] || k}: ${n}`)
        .join(" · ");
}

function SourceBackfillPanel({ onReclassified }: { onReclassified: () => Promise<void> }) {
    const [busy, setBusy] = useState<null | "preview" | "run">(null);
    const [lastResult, setLastResult] = useState<BackfillResult | null>(null);

    const run = async (dryRun: boolean) => {
        if (busy) return;
        if (!dryRun) {
            const ok = window.confirm(
                "Alle bestehenden Analytics-Events werden anhand der aktuellen Meta/TikTok-Regeln neu klassifiziert.\n\n" +
                "Die Aktion ist idempotent und kann mehrfach ausgeführt werden — sie ändert nur das Feld \u201eresolved_source\u201c, keine Original-Daten.\n\nJetzt reklassifizieren?",
            );
            if (!ok) return;
        }
        setBusy(dryRun ? "preview" : "run");
        try {
            const res = await api<BackfillResult>(
                `/api/admin/analytics/backfill-source${dryRun ? "?dry_run=true" : ""}`,
                { method: "POST" },
            );
            if (!res.ok || !res.data) {
                toast.error(res.error || "Backfill fehlgeschlagen.");
                return;
            }
            setLastResult(res.data);
            const dist = formatBackfillSources(res.data.by_source);
            if (dryRun) {
                toast.success(
                    `Vorschau: ${res.data.reclassified} von ${res.data.total_scanned} Events würden reklassifiziert werden. Verteilung: ${dist}`,
                    { duration: 8000 },
                );
            } else {
                toast.success(
                    `Erfolgreich · ${res.data.reclassified} Events reklassifiziert (${res.data.total_scanned} geprüft). Verteilung: ${dist}`,
                    { duration: 8000 },
                );
                await onReclassified();
            }
        } finally {
            setBusy(null);
        }
    };

    return (
        <div
            className="mb-4 p-3 rounded-sm"
            style={{
                background: "hsl(var(--hf-bg-alt, var(--hf-bg)))",
                border: "1px dashed hsl(var(--hf-line))",
            }}
            data-testid="source-backfill-panel"
        >
            <div className="flex items-start gap-2 mb-2">
                <Sparkles size={14} className="text-hf-gold mt-0.5 shrink-0" />
                <div className="text-xs text-hf-muted leading-snug">
                    <span className="text-hf-ink font-medium">Attribution reklassifizieren.</span>{" "}
                    Weist alten Klicks (vor dem Meta/TikTok-Update) die richtige Quelle zu.
                    Idempotent — kann bedenkenlos mehrfach ausgeführt werden.
                </div>
            </div>
            <div className="flex gap-2 flex-wrap">
                <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => void run(true)}
                    data-testid="backfill-preview-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition disabled:opacity-50"
                    style={{
                        borderColor: "hsl(var(--hf-line))",
                        color: "hsl(var(--hf-ink))",
                        background: "transparent",
                    }}
                >
                    {busy === "preview" ? (
                        <RefreshCw size={12} className="animate-spin" />
                    ) : (
                        <Eye size={12} />
                    )}
                    Vorschau (Dry-Run)
                </button>
                <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => void run(false)}
                    data-testid="backfill-run-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition disabled:opacity-50"
                    style={{
                        borderColor: "hsl(var(--hf-gold))",
                        color: "hsl(var(--hf-bg))",
                        background: "hsl(var(--hf-gold))",
                    }}
                >
                    {busy === "run" ? (
                        <RefreshCw size={12} className="animate-spin" />
                    ) : (
                        <Sparkles size={12} />
                    )}
                    Jetzt reklassifizieren
                </button>
                {lastResult && (
                    <span
                        className="text-[11px] text-hf-muted self-center ml-1"
                        data-testid="backfill-last-result"
                    >
                        Letzter Lauf: {lastResult.reclassified} von {lastResult.total_scanned}{" "}
                        {lastResult.dry_run ? "würden aktualisiert" : "aktualisiert"} in {lastResult.duration_ms} ms
                    </span>
                )}
            </div>
        </div>
    );
}


function PanelCard({
    title, icon, testId, children,
}: {
    title: string; icon: React.ReactNode; testId: string; children: React.ReactNode;
}) {
    return (
        <div
            className="p-6 rounded-sm"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-hf-muted text-xs">
                {icon}<span className="hf-eyebrow" style={{ fontSize: 10 }}>{title}</span>
            </div>
            <div className="mt-4">{children}</div>
        </div>
    );
}


/** Abbruch-Analyse-Sektion. Enthält 2 Sub-Karten:
 *   - Top Exit-Pages (Seitenpfad, Count, %, dezenter Balken)
 *   - Letzte Aktion vor Abbruch (menschenlesbare Kategorie, Count, %)
 *  Nutzt lokalen Range-Toggle (7d/30d) analog zu den bestehenden Cards.
 *  Enthält zusätzlich einen einmaligen Cleanup-Button, der bestehende
 *  Scanner-Pfade aus der DB entfernt (siehe `POST /api/admin/analytics/
 *  purge-route-scans`). Idempotent — 0 Löschungen bei sauberer DB.
 */
function ExitAnalysisSection({
    data, range, onRangeChange, loading,
}: {
    data: ExitAnalysis | null;
    range: string;
    onRangeChange: (r: string) => void;
    loading: boolean;
}) {
    const rangeLabel = range === "7d" ? "7 Tage" : range === "30d" ? "30 Tage" : range === "today" ? "Heute" : range === "yesterday" ? "Gestern" : "Zeitraum";
    const empty = !loading && (!data || data.total_sessions === 0);
    const [purging, setPurging] = useState(false);
    const [purgeResult, setPurgeResult] = useState<string | null>(null);
    const [purgingFallback, setPurgingFallback] = useState(false);
    const [purgingEmpty, setPurgingEmpty] = useState(false);
    const runPurge = async () => {
        if (purging) return;
        const ok = window.confirm(
            "Alle Analytics-Events mit unbekannten Pfaden werden dauerhaft aus der Datenbank gelöscht. Fortfahren?",
        );
        if (!ok) return;
        setPurging(true);
        setPurgeResult(null);
        const res = await api<{ deleted: number; scanner_paths_count: number }>(
            "/api/admin/analytics/purge-route-scans",
            { method: "POST" },
        );
        setPurging(false);
        if (res.ok && res.data) {
            setPurgeResult(
                res.data.deleted > 0
                    ? `${res.data.deleted} Event${res.data.deleted === 1 ? "" : "s"} aus ${res.data.scanner_paths_count} Scanner-Pfad${res.data.scanner_paths_count === 1 ? "" : "en"} gelöscht.`
                    : "Bereits sauber — keine Scanner-Events gefunden.",
            );
            // Card-Daten neu holen — einfachster Weg: Range-Wechsel-Trigger.
            onRangeChange(range);
        } else {
            setPurgeResult(res.error || "Cleanup fehlgeschlagen.");
        }
    };
    const runFallbackPurge = async () => {
        if (purgingFallback) return;
        const ok = window.confirm(
            "Alte Analytics-Events mit unbekannten Medien-IDs bereinigen? Die Events bleiben erhalten, nur die ungültige Media-ID wird geleert.",
        );
        if (!ok) return;
        setPurgingFallback(true);
        const res = await api<{ modified: number; distinct_ids: number; sample: string[] }>(
            "/api/admin/analytics/purge-fallback-media-ids",
            { method: "POST" },
        );
        setPurgingFallback(false);
        if (res.ok && res.data) {
            if (res.data.modified === 0) {
                toast.success("Alles sauber – keine Fallback-IDs gefunden.");
            } else {
                toast.success(
                    `${res.data.modified} Event${res.data.modified === 1 ? "" : "s"} bereinigt · ${res.data.distinct_ids} verschiedene ID${res.data.distinct_ids === 1 ? "" : "s"} entfernt.`,
                );
                onRangeChange(range);
            }
        } else {
            toast.error(res.error || "Cleanup fehlgeschlagen.");
        }
    };
    const runEmptyPurge = async () => {
        if (purgingEmpty) return;
        const ok = window.confirm(
            "Alte Interaktions-Events ohne Media-Bezug endgültig löschen? Diese Events werden vollständig aus der Datenbank entfernt (nicht nur bereinigt).",
        );
        if (!ok) return;
        setPurgingEmpty(true);
        const res = await api<{ deleted: number }>(
            "/api/admin/analytics/purge-empty-media-interactions",
            { method: "POST" },
        );
        setPurgingEmpty(false);
        if (res.ok && res.data) {
            if (res.data.deleted === 0) {
                toast.success("Keine leeren Interaktionen gefunden.");
            } else {
                toast.success(
                    `${res.data.deleted} Placeholder-Interaktion${res.data.deleted === 1 ? "" : "en"} gelöscht.`,
                );
                onRangeChange(range);
            }
        } else {
            toast.error(res.error || "Cleanup fehlgeschlagen.");
        }
    };
    return (
        <div
            className="p-6 rounded-sm"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid="analytics-exit-analysis"
        >
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="hf-eyebrow">Verlassen &amp; Absprünge</div>
                    <div className="font-display text-xl mt-1 flex items-center gap-2">
                        <LogOut size={18} /> Wo brechen Besucher ab?
                    </div>
                    <div className="mt-2 text-xs text-hf-muted max-w-xl">
                        Letzte Seite und letzte Aktion pro Session im Zeitraum {rangeLabel}
                        {typeof data?.total_sessions === "number" && (
                            <> · Basis: <strong>{data.total_sessions}</strong> Session{data.total_sessions === 1 ? "" : "s"}</>
                        )}.
                    </div>
                </div>
                <div className="inline-flex flex-col items-end gap-2">
                    <AnalyticsRangeFilter
                        value={range}
                        onChange={onRangeChange}
                        testIdPrefix="analytics-exit-range"
                    />
                    <button
                        type="button"
                        onClick={() => void runPurge()}
                        disabled={purging}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                        style={{
                            background: "transparent",
                            color: "hsl(var(--hf-muted))",
                            border: "1px solid hsl(var(--hf-line))",
                            cursor: purging ? "wait" : "pointer",
                            opacity: purging ? 0.7 : 1,
                        }}
                        title="Löscht bestehende Bot-/Scanner-Zeilen (Pfade, die keiner Frontend-Route entsprechen) einmalig aus der Analytics-Datenbank."
                        data-testid="analytics-exit-purge"
                    >
                        <AlertTriangle size={11} strokeWidth={2.2} />
                        {purging ? "Wird bereinigt…" : "Alt-Botdaten löschen"}
                    </button>
                    <button
                        type="button"
                        onClick={() => void runFallbackPurge()}
                        disabled={purgingFallback}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                        style={{
                            background: "transparent",
                            color: "hsl(var(--hf-muted))",
                            border: "1px solid hsl(var(--hf-line))",
                            cursor: purgingFallback ? "wait" : "pointer",
                            opacity: purgingFallback ? 0.7 : 1,
                        }}
                        title="Bereinigt in bestehenden Analytics-Events alte Fallback-Media-IDs (`src-*`), die aus früherem Tracking ohne echte DB-UUID stammen. Events bleiben erhalten, nur die ungültige media_id wird auf null gesetzt."
                        data-testid="analytics-purge-fallback-media"
                    >
                        <ImageOff size={11} strokeWidth={2.2} />
                        {purgingFallback ? "Wird bereinigt…" : "Fallback-Media-IDs bereinigen"}
                    </button>
                    <button
                        type="button"
                        onClick={() => void runEmptyPurge()}
                        disabled={purgingEmpty}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                        style={{
                            background: "transparent",
                            color: "hsl(var(--hf-muted))",
                            border: "1px solid hsl(var(--hf-line))",
                            cursor: purgingEmpty ? "wait" : "pointer",
                            opacity: purgingEmpty ? 0.7 : 1,
                        }}
                        title="Löscht endgültig alle Interaktions-Events ohne Media-Bezug (media_id null). Betrifft historische Placeholder-Zeilen, die auch nach dem Fallback-ID-Cleanup übrig geblieben sind."
                        data-testid="analytics-purge-empty-interactions"
                    >
                        <ImageOff size={11} strokeWidth={2.2} style={{ opacity: 0.55 }} />
                        {purgingEmpty ? "Wird gelöscht…" : "Placeholder-Interaktionen löschen"}
                    </button>
                    {purgeResult && (
                        <span
                            className="text-[10px]"
                            style={{ color: "hsl(var(--hf-muted))" }}
                            data-testid="analytics-exit-purge-result"
                        >
                            {purgeResult}
                        </span>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="mt-6 py-8 text-center text-hf-muted text-sm">Wird geladen…</div>
            ) : empty ? (
                <div className="mt-6 py-8 text-center text-hf-muted text-sm" data-testid="analytics-exit-empty">
                    Noch keine Sessions im Zeitraum {rangeLabel} — sobald Besucher unterwegs sind,
                    erscheinen hier die Abbruch-Muster.
                </div>
            ) : (
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ExitPagesCard exits={data?.exits || []} />
                    <ExitActionsCard actions={data?.actions || []} />
                </div>
            )}
        </div>
    );
}

function ExitPagesCard({
    exits,
}: {
    exits: ExitAnalysis["exits"];
}) {
    const maxPct = Math.max(1, ...exits.map((e) => e.percentage));
    return (
        <div data-testid="analytics-exit-pages">
            <div className="hf-eyebrow flex items-center gap-2" style={{ fontSize: 10 }}>
                <Eye size={12} /> Top Exit-Seiten
            </div>
            <ul className="mt-3 space-y-1">
                {exits.length === 0 && (
                    <li className="text-hf-muted text-sm">Keine Exits erfasst.</li>
                )}
                {exits.map((e) => (
                    <ExitPageRow key={e.path} path={e.path} count={e.count} percentage={e.percentage} maxPct={maxPct} />
                ))}
            </ul>
        </div>
    );
}

/** Prüft, ob ein Pfad zu einer öffentlichen Frontend-Route in `App.js`
 *  matched. Bot-/Scanner-Traffic (wie `/retest`, `/latency`, `/delta-2`,
 *  `/testseite-fresh-abc`) fällt hier durch und wird visuell als
 *  „nicht existierende Route" markiert. Query-Strings & Trailing-Slashes
 *  werden ignoriert. */
function isKnownPublicRoute(rawPath: string): boolean {
    if (!rawPath || rawPath === "—") return false;
    // Query + Hash abtrennen, Trailing-Slash normalisieren.
    const noQuery = rawPath.split("?")[0].split("#")[0];
    const normalized = noQuery.length > 1 && noQuery.endsWith("/")
        ? noQuery.slice(0, -1)
        : noQuery;
    const known = new Set(["", "/", "/sylt", "/erleben", "/impressum", "/datenschutz", "/bdv-kontakt"]);
    if (known.has(normalized)) return true;
    // /projekt/:slug — jede projekt-URL zulassen (Preview würde 404 zeigen,
    // aber der Nutzer weiß es).
    if (normalized.startsWith("/projekt/") && normalized.length > "/projekt/".length) return true;
    // Rep-Tracking `/1` … `/99`.
    const m = normalized.match(/^\/(\d{1,2})$/);
    if (m) {
        const n = parseInt(m[1], 10);
        if (n >= 1 && n <= 99) return true;
    }
    return false;
}

/** Einzelne Exit-Page-Zeile: als `<a target="_blank">` gerendert, damit
 *  der Admin-Tab (Scroll-Position, State) komplett unberührt bleibt.
 *  Für Bot-/Scanner-Pfade wird zusätzlich ein „nicht existierende
 *  Route"-Warnhinweis eingeblendet — der Link bleibt aber klickbar,
 *  falls der Nutzer nachschauen möchte. */
function ExitPageRow({
    path, count, percentage, maxPct,
}: {
    path: string; count: number; percentage: number; maxPct: number;
}) {
    const knownRoute = isKnownPublicRoute(path);
    const clickable = path && path !== "—";
    const barPct = Math.min(100, (percentage / maxPct) * 100);

    const inner = (
        <>
            <div className="flex flex-col min-w-0 flex-1">
                <span
                    className="font-mono text-xs truncate"
                    title={path}
                >
                    {path}
                </span>
                {!knownRoute && clickable && (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] mt-0.5"
                        style={{ color: "hsl(var(--hf-muted))" }}
                        data-testid="analytics-exit-page-unknown"
                    >
                        <AlertTriangle size={10} strokeWidth={2} />
                        nicht existierende Route
                    </span>
                )}
            </div>
            <div
                className="relative rounded-sm"
                style={{
                    width: 120,
                    height: 6,
                    background: "hsl(var(--hf-line) / 0.6)",
                    flex: "0 0 auto",
                }}
                aria-hidden
            >
                <div
                    className="absolute inset-y-0 left-0 rounded-sm"
                    style={{
                        width: `${barPct}%`,
                        background: knownRoute
                            ? "hsl(var(--hf-gold-dark))"
                            : "hsl(var(--hf-muted) / 0.5)",
                        transition: "width 200ms ease",
                    }}
                />
            </div>
            <div className="text-xs text-hf-muted tabular-nums whitespace-nowrap" style={{ width: 90, textAlign: "right" }}>
                <span className="text-hf-ink font-medium">{count}</span>
                <span className="ml-1">({percentage.toFixed(1)}%)</span>
            </div>
            {clickable && (
                <ExternalLink
                    size={12}
                    strokeWidth={2}
                    className="text-hf-muted flex-shrink-0"
                    style={{ opacity: 0.7 }}
                    aria-hidden
                />
            )}
        </>
    );

    const commonRowStyle: React.CSSProperties = {
        border: "1px solid transparent",
        borderRadius: 4,
        transition: "background-color 120ms ease, border-color 120ms ease",
    };

    // Nicht-klickbare Zeile (leerer Pfad „—") — nur Anzeige.
    if (!clickable) {
        return (
            <li
                className="flex items-center gap-3 px-2 py-1.5"
                style={commonRowStyle}
                data-testid={`analytics-exit-page-${encodeURIComponent(path)}`}
            >
                {inner}
            </li>
        );
    }

    return (
        <li
            data-testid={`analytics-exit-page-${encodeURIComponent(path)}`}
            data-known-route={knownRoute ? "true" : "false"}
        >
            <a
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Öffne Seite ${path} in neuem Tab`}
                className="flex items-center gap-3 px-2 py-1.5 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                style={commonRowStyle}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = "hsl(var(--hf-gold-soft) / 0.35)";
                    e.currentTarget.style.borderColor = "hsl(var(--hf-gold-dark) / 0.4)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "transparent";
                }}
            >
                {inner}
            </a>
        </li>
    );
}

function ExitActionsCard({
    actions,
}: {
    actions: ExitAnalysis["actions"];
}) {
    const maxPct = Math.max(1, ...actions.map((a) => a.percentage));
    return (
        <div data-testid="analytics-exit-actions">
            <div className="hf-eyebrow flex items-center gap-2" style={{ fontSize: 10 }}>
                <MousePointer size={12} /> Letzte Aktion vor Abbruch
            </div>
            <ul className="mt-3 space-y-2">
                {actions.length === 0 && (
                    <li className="text-hf-muted text-sm">Keine Aktionen erfasst.</li>
                )}
                {actions.map((a) => {
                    const label = EXIT_ACTION_LABEL[a.type] || a.type;
                    return (
                        <li
                            key={a.type}
                            className="flex items-center gap-3"
                            data-testid={`analytics-exit-action-${a.type}`}
                        >
                            <div className="text-sm truncate flex-1 min-w-0" title={a.type}>
                                {label}
                            </div>
                            <div
                                className="relative rounded-sm"
                                style={{
                                    width: 120,
                                    height: 6,
                                    background: "hsl(var(--hf-line) / 0.6)",
                                    flex: "0 0 auto",
                                }}
                                aria-hidden
                            >
                                <div
                                    className="absolute inset-y-0 left-0 rounded-sm"
                                    style={{
                                        width: `${Math.min(100, (a.percentage / maxPct) * 100)}%`,
                                        background: "hsl(var(--hf-gold-dark))",
                                        transition: "width 200ms ease",
                                    }}
                                />
                            </div>
                            <div className="text-xs text-hf-muted tabular-nums whitespace-nowrap" style={{ width: 90, textAlign: "right" }}>
                                <span className="text-hf-ink font-medium">{a.count}</span>
                                <span className="ml-1">({a.percentage.toFixed(1)}%)</span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}


/** Analytics-Karte „360°-CTA Wirkung". Zeigt Anzeigen, Klicks, Dismissals
 *  + Klick-/Dismiss-Rate + Split nach Trigger-Typ (photo360/video360). */
function CtaImpactSection({
    data, range, onRangeChange, loading,
}: {
    data: CtaImpact | null;
    range: string;
    onRangeChange: (r: string) => void;
    loading: boolean;
}) {
    const empty = !loading && (!data || data.shown === 0);
    const TRIGGER_LABEL: Record<string, string> = {
        photo360: "Nach 360°-Foto",
        video360: "Nach 360°-Video",
        before_after: "Nach Vorher/Nachher",
        unknown: "Unbekannter Trigger",
    };
    return (
        <div
            className="p-6 rounded-sm"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid="analytics-cta-impact"
        >
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="hf-eyebrow">Post-Viewer-Wirkung</div>
                    <div className="font-display text-xl mt-1">360°-CTA (&bdquo;Beeindruckt?&ldquo;)</div>
                    <div className="mt-2 text-xs text-hf-muted max-w-xl">
                        Wie oft wurde die Card nach 360°-Erlebnissen eingeblendet, geklickt oder weggeklickt.
                    </div>
                </div>
                <AnalyticsRangeFilter
                    value={range}
                    onChange={onRangeChange}
                    testIdPrefix="analytics-cta-range"
                />
            </div>

            {loading ? (
                <div className="mt-6 py-8 text-center text-hf-muted text-sm">Wird geladen…</div>
            ) : empty ? (
                <div className="mt-6 py-8 text-center text-hf-muted text-sm" data-testid="analytics-cta-empty">
                    Noch keine Daten. Sobald die Card auf der Live-Seite eingeblendet wird, erscheinen hier Zahlen.
                </div>
            ) : data && (
                <>
                    <div className="mt-6 grid grid-cols-3 gap-4">
                        <CtaKpi label="Anzeigen" value={data.shown} subtext="Card wurde eingeblendet" testId="cta-kpi-shown" />
                        <CtaKpi
                            label="Klicks"
                            value={data.clicked}
                            subtext={`${data.click_rate.toFixed(1)} % Klickrate`}
                            highlight={data.click_rate >= 5}
                            testId="cta-kpi-clicked"
                        />
                        <CtaKpi
                            label="Wegklicks"
                            value={data.dismissed}
                            subtext={`${data.dismiss_rate.toFixed(1)} % Dismiss-Rate`}
                            testId="cta-kpi-dismissed"
                        />
                    </div>
                    {Object.keys(data.by_trigger).length > 0 && (
                        <div className="mt-6">
                            <div className="hf-eyebrow" style={{ fontSize: 10 }}>Nach Auslöser</div>
                            <table className="w-full text-sm mt-3">
                                <thead>
                                    <tr className="text-left text-hf-muted" style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                                        <th className="py-2 font-normal text-xs uppercase tracking-wider">Auslöser</th>
                                        <th className="py-2 font-normal text-xs uppercase tracking-wider text-right">Anzeigen</th>
                                        <th className="py-2 font-normal text-xs uppercase tracking-wider text-right">Klicks</th>
                                        <th className="py-2 font-normal text-xs uppercase tracking-wider text-right">Klickrate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(data.by_trigger).map(([trig, v]) => {
                                        const rate = v.shown ? (v.clicked / v.shown) * 100 : 0;
                                        return (
                                            <tr key={trig} data-testid={`cta-trigger-${trig}`}>
                                                <td className="py-2">{TRIGGER_LABEL[trig] || trig}</td>
                                                <td className="py-2 text-right tabular-nums">{v.shown}</td>
                                                <td className="py-2 text-right tabular-nums">{v.clicked}</td>
                                                <td className="py-2 text-right tabular-nums text-hf-muted">{rate.toFixed(1)} %</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function CtaKpi({
    label, value, subtext, highlight, testId,
}: {
    label: string; value: number; subtext: string; highlight?: boolean; testId: string;
}) {
    return (
        <div
            className="p-4 rounded-sm"
            style={{
                background: highlight ? "hsl(var(--hf-gold-soft) / 0.35)" : "hsl(var(--hf-surface))",
                border: `1px solid ${highlight ? "hsl(var(--hf-gold-dark) / 0.5)" : "hsl(var(--hf-line))"}`,
            }}
            data-testid={testId}
        >
            <div className="text-xs uppercase tracking-wider text-hf-muted">{label}</div>
            <div className="text-3xl font-display mt-1 tabular-nums">{value}</div>
            <div className="text-xs text-hf-muted mt-1">{subtext}</div>
        </div>
    );
}

/** Analytics-Karte „Referenz-Aktion Wirkung". */
function AktionImpactSection({
    data, range, onRangeChange, loading,
}: {
    data: AktionImpact | null;
    range: string;
    onRangeChange: (r: string) => void;
    loading: boolean;
}) {
    const empty = !loading && (!data || (data.banner_shown === 0 && data.section_shown === 0 && data.form_submits_from_db === 0));
    return (
        <div
            className="p-6 rounded-sm"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid="analytics-aktion-impact"
        >
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="hf-eyebrow">Kampagnen-Wirkung</div>
                    <div className="font-display text-xl mt-1">Referenz-Aktion</div>
                    <div className="mt-2 text-xs text-hf-muted max-w-xl">
                        Sichtbarkeit &amp; Konversion der UTM-getriggerten Referenz-Aktion.
                    </div>
                </div>
                <AnalyticsRangeFilter
                    value={range}
                    onChange={onRangeChange}
                    testIdPrefix="analytics-aktion-range"
                />
            </div>
            {loading ? (
                <div className="mt-6 py-8 text-center text-hf-muted text-sm">Wird geladen…</div>
            ) : empty ? (
                <div className="mt-6 py-8 text-center text-hf-muted text-sm" data-testid="analytics-aktion-empty">
                    Noch keine Aktions-Interaktionen. Sobald Besucher mit passendem UTM-Link ankommen, erscheinen hier Zahlen.
                </div>
            ) : data && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <AktionKpi
                        label="Streifen-Impressions"
                        value={data.banner_shown}
                        subtext={`${data.banner_clicks} Klicks · ${data.banner_click_rate.toFixed(1)} %`}
                        testId="aktion-kpi-banner"
                    />
                    <AktionKpi
                        label="Sektion-Impressions"
                        value={data.section_shown}
                        subtext={`${data.section_clicks} Klicks · ${data.section_click_rate.toFixed(1)} %`}
                        testId="aktion-kpi-section"
                    />
                    <AktionKpi
                        label="Anfragen aus Aktion"
                        value={data.form_submits_from_db}
                        subtext={data.form_submits !== data.form_submits_from_db
                            ? `${data.form_submits} Events getrackt`
                            : "aus Datenbank gezählt"}
                        highlight={data.form_submits_from_db > 0}
                        testId="aktion-kpi-form"
                    />
                    <AktionKpi
                        label="Konversion"
                        value={data.conversion_rate}
                        suffix="%"
                        subtext="Anfragen / Sektion-Impressions"
                        highlight={data.conversion_rate >= 3}
                        testId="aktion-kpi-conversion"
                    />
                </div>
            )}
        </div>
    );
}

function AktionKpi({
    label, value, subtext, suffix, highlight, testId,
}: {
    label: string; value: number; subtext: string; suffix?: string; highlight?: boolean; testId: string;
}) {
    return (
        <div
            className="p-4 rounded-sm"
            style={{
                background: highlight ? "hsl(var(--hf-gold-soft) / 0.35)" : "hsl(var(--hf-surface))",
                border: `1px solid ${highlight ? "hsl(var(--hf-gold-dark) / 0.5)" : "hsl(var(--hf-line))"}`,
            }}
            data-testid={testId}
        >
            <div className="text-xs uppercase tracking-wider text-hf-muted">{label}</div>
            <div className="text-3xl font-display mt-1 tabular-nums">
                {value}{suffix || ""}
            </div>
            <div className="text-xs text-hf-muted mt-1">{subtext}</div>
        </div>
    );
}

